/**
 * Event Manager
 * 
 * Handles network event management and coordination.
 * Provides event listener management, event emission and broadcasting,
 * cross-service event coordination, event filtering/throttling, and cleanup.
 */

import { ConnectionStatus } from '@capacitor/network';
import { NetworkEvent, NetworkEventListener } from './BaseNetworkService';

/**
 * Event throttling configuration
 */
interface ThrottleConfig {
  enabled: boolean;
  interval: number; // milliseconds
  maxEvents: number; // max events per interval
}

/**
 * Event Manager
 * 
 * Centralized event management system for network events.
 * Handles listener registration, event emission, throttling, and cleanup.
 */
export class EventManager {
  private eventListeners: Set<NetworkEventListener> = new Set();
  private eventHistory: Array<{ event: NetworkEvent; timestamp: number }> = [];
  private throttleConfig: ThrottleConfig;
  private enableDebugLogging: boolean;

  // Throttling state
  private eventCounts: Map<string, number> = new Map();
  private throttleResetTimer: NodeJS.Timeout | null = null;

  constructor(
    throttleConfig: ThrottleConfig = { enabled: true, interval: 1000, maxEvents: 10 },
    enableDebugLogging = true
  ) {
    this.throttleConfig = throttleConfig;
    this.enableDebugLogging = enableDebugLogging;

    if (this.throttleConfig.enabled) {
      this.startThrottleReset();
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: NetworkEventListener): void {
    this.eventListeners.add(listener);
    this.debugLog(`📡 Event listener added. Total listeners: ${this.eventListeners.size}`);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: NetworkEventListener): void {
    const removed = this.eventListeners.delete(listener);
    if (removed) {
      this.debugLog(`📡 Event listener removed. Total listeners: ${this.eventListeners.size}`);
    }
  }

  /**
   * Remove all event listeners
   */
  removeAllEventListeners(): void {
    const count = this.eventListeners.size;
    this.eventListeners.clear();
    this.debugLog(`📡 All event listeners removed. Removed: ${count}`);
  }

  /**
   * Emit event to all listeners
   */
  emitEvent(event: NetworkEvent): void {
    // Check throttling
    if (this.throttleConfig.enabled && this.isEventThrottled(event.type)) {
      this.debugLog(`🚫 Event throttled: ${event.type}`);
      return;
    }

    // Add to history
    this.addToHistory(event);

    // Emit to all listeners
    let successCount = 0;
    let errorCount = 0;

    this.eventListeners.forEach(listener => {
      try {
        listener(event);
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`❌ Error in network event listener for ${event.type}:`, error);
      }
    });

    this.debugLog(`📡 Event emitted: ${event.type}`, {
      listeners: this.eventListeners.size,
      successful: successCount,
      errors: errorCount,
      data: event.data
    });

    // Update throttle count
    if (this.throttleConfig.enabled) {
      this.incrementEventCount(event.type);
    }
  }

  /**
   * Emit sync triggered event
   */
  emitSyncTriggered(trigger: string, networkStatus: ConnectionStatus): void {
    this.emitEvent({
      type: 'sync_triggered',
      data: {
        trigger,
        networkStatus
      }
    });
  }

  /**
   * Get event history
   */
  getEventHistory(): Array<{ event: NetworkEvent; timestamp: number }> {
    return [...this.eventHistory];
  }

  /**
   * Get recent events of a specific type
   */
  getRecentEvents(eventType: NetworkEvent['type'], maxAge = 60000): NetworkEvent[] {
    const cutoff = Date.now() - maxAge;
    return this.eventHistory
      .filter(entry => entry.timestamp >= cutoff && entry.event.type === eventType)
      .map(entry => entry.event);
  }

  /**
   * Get event statistics
   */
  getEventStats(): {
    totalEvents: number;
    eventsByType: Record<string, number>;
    recentEvents: number;
    listenersCount: number;
  } {
    const eventsByType: Record<string, number> = {};
    const recentCutoff = Date.now() - 60000; // Last minute
    let recentEvents = 0;

    this.eventHistory.forEach(entry => {
      const type = entry.event.type;
      eventsByType[type] = (eventsByType[type] || 0) + 1;
      
      if (entry.timestamp >= recentCutoff) {
        recentEvents++;
      }
    });

    return {
      totalEvents: this.eventHistory.length,
      eventsByType,
      recentEvents,
      listenersCount: this.eventListeners.size
    };
  }

  /**
   * Update throttle configuration
   */
  updateThrottleConfig(config: Partial<ThrottleConfig>): void {
    this.throttleConfig = { ...this.throttleConfig, ...config };
    
    if (this.throttleConfig.enabled && !this.throttleResetTimer) {
      this.startThrottleReset();
    } else if (!this.throttleConfig.enabled && this.throttleResetTimer) {
      this.stopThrottleReset();
    }

    this.debugLog('📡 Throttle configuration updated:', this.throttleConfig);
  }

  /**
   * Check if event type is currently throttled
   */
  private isEventThrottled(eventType: string): boolean {
    const count = this.eventCounts.get(eventType) || 0;
    return count >= this.throttleConfig.maxEvents;
  }

  /**
   * Increment event count for throttling
   */
  private incrementEventCount(eventType: string): void {
    const current = this.eventCounts.get(eventType) || 0;
    this.eventCounts.set(eventType, current + 1);
  }

  /**
   * Start throttle reset timer
   */
  private startThrottleReset(): void {
    if (this.throttleResetTimer) return;

    this.throttleResetTimer = setInterval(() => {
      this.eventCounts.clear();
    }, this.throttleConfig.interval);
  }

  /**
   * Stop throttle reset timer
   */
  private stopThrottleReset(): void {
    if (this.throttleResetTimer) {
      clearInterval(this.throttleResetTimer);
      this.throttleResetTimer = null;
    }
  }

  /**
   * Add event to history
   */
  private addToHistory(event: NetworkEvent): void {
    this.eventHistory.push({
      event,
      timestamp: Date.now()
    });

    // Keep only last 1000 events
    if (this.eventHistory.length > 1000) {
      this.eventHistory = this.eventHistory.slice(-1000);
    }
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
    this.debugLog('📡 Event history cleared');
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.enableDebugLogging) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * Get current listener count
   */
  getListenerCount(): number {
    return this.eventListeners.size;
  }

  /**
   * Check if has listeners
   */
  hasListeners(): boolean {
    return this.eventListeners.size > 0;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.removeAllEventListeners();
    this.clearHistory();
    this.stopThrottleReset();
    this.eventCounts.clear();
    
    this.debugLog('🧹 Event manager cleaned up');
  }
}
